import React, { useState } from 'react';
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import '../../../App.css';
import { apiUrlPrefix } from '../../../config';
import useStore from '../state-files/zustondStoreStates';
import fetchDiscussions from '../helper-methods/fetchDiscussions';
import Cookies from 'js-cookie';
import GetUserCommunities from '../Components/getUserCommunities';
import GetUsers from '../Components/getUsers';
import getAllUsers from '../helper-methods/getAllUsers';

const CreateDiscussionWidget = ({ closeMenu, isBold }) => {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [currentUser, setName] = useState();
  const setDiscussions = useStore((state) => state.setDiscussions);
  const { currentCommunityId, currentCommunityName } = useStore(); // Use zustand store

  // Use TanStack Query to manage the user list
  const { data: usersData } = useQuery({
    queryKey: ['allUsers'],
    queryFn: () => getAllUsers(setName),
    enabled: open, // Only fetch when the dialog is opened
  });

  // --- FETCHING Communities ---
  const getAllCommunities = async () => {
    try {
      const response = await fetch(apiUrlPrefix + "Communities/getUserCommunities?userId=" + localStorage.getItem('userId'), {
        method: "GET",
        credentials: "include",
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        // 1. Capture the data in a variable
        const data = await response.json();

        return await data;
      }
      return [];
    } catch (error) {
      console.error("Fetch failed:", error);
      return [];
    }
  };

  // Use TanStack Query to manage the user list
  const { data: communityData } = useQuery({
    queryKey: ['allCommunities'],
    queryFn: getAllCommunities,
    enabled: open, // Only fetch when the dialog is opened
  });

  // --- MUTATION LOGIC ---
  const createDiscussion = async ({ title, users, communities }) => {
    // Since you are using 'multiple' in Autocomplete, 
    // 'users' is already the array you need!
    const apiUrl = apiUrlPrefix + 'Discussion/createDiscussion';
    var communityIds = [];

    //Checks if the user is wishing to make a community chat or a private discussion
    if (communities && communities.length > 0) {
      // 1. Map the names (from Formik) back to IDs (from usersData)
      communityIds = communities.map(selectedName => {
        // If 'All' is selected, handle it based on your backend logic 
        // (usually you'd send a special ID or the string 'All')
        if (selectedName === 'All') return 'All';
        if (selectedName === currentUser) return localStorage.getItem('userId');;

        // Find the original user object that matches the selected name
        const foundUser = communityData.find(u => u.name === selectedName);

        // Return the ID if found, otherwise return the name as a fallback
        return foundUser ? foundUser.id : selectedName;

      })
    };

    /* 
      
      
              USERS 



    */
    // 1. Map the names (from Formik) back to IDs (from usersData)
    const userIds = users.map(selectedName => {
      // If 'All' is selected, handle it based on your backend logic 
      // (usually you'd send a special ID or the string 'All')
      if (selectedName === 'All') return 'All';
      if (selectedName === currentUser) return localStorage.getItem('userId');;

      // Find the original user object that matches the selected name
      const foundUser = usersData.find(u => u.name === selectedName);

      // Return the ID if found, otherwise return the name as a fallback
      return foundUser ? foundUser.id : selectedName;
    });

    // 2. Extra safety check for "All"
    let finalUsers = userIds;
    if (finalUsers.length > 1 && finalUsers.includes("All")) {
      finalUsers = finalUsers.filter(id => id !== "All");
      formik.setFieldValue("users", finalUsers); // Update UI state
    }

    var communityIdValue = [];

    if(currentCommunityId != null && currentCommunityId != '') {
      communityIdValue = [currentCommunityId];
    }
    var currentUserId = localStorage.getItem('userId');; //Makes the current user an admin
    const response = await fetch(apiUrl, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        Title: title,
        Users: finalUsers, // Send the array directly
        communityId: communityIdValue,
        Messages: [],
        Admins: [currentUserId]
      }),
    });

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Invalid credentials or permissions.');
      }
      throw new Error('Network response was not ok');
    }
    return response.json();
  };

  const createDiscussionMutation = useMutation({
    mutationFn: createDiscussion,
    onSuccess: (data) => {
      if (data) {
        if(currentCommunityName == null && currentCommunityName == "") { fetchDiscussions(setDiscussions); }
        else { fetchDiscussions(setDiscussions, currentCommunityId) }
        handleClose();
      } else {
        throw new Error('Unexpected response structure');
      }
    },
    onError: (error) => {
      setError(error.message);
    },
  });

  // --- DIALOG CONTROL ---
  const handleClickOpen = (e) => {
    e.stopPropagation(); // Prevent Menu closing until we want it to
    setOpen(true);
  };

  const handleClose = () => {
    setError(null);
    setOpen(false);
    if (closeMenu) closeMenu();
  };

  const formik = useFormik({
    initialValues: {
      title: '',
      users: ['All'],
    },
    validationSchema: Yup.object({
      title: Yup.string().required('Title is required'),
      users: Yup.array()
        .of(Yup.string())
        .min(1, 'At least one user is required')
        .required('At least one user is required'),
    }),
    onSubmit: (values) => {
      setError(null);
      createDiscussionMutation.mutate(values);
    },
  });

  return (
    <div>
      <Button
        id="loginButton"
        variant="contained"
        color="none"
        onClick={handleClickOpen}
        style={{
          fontWeight: isBold ? 'bold' : 'none',
          boxShadow: 'none'
        }}
      >
        Create Discussion
      </Button>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Create Discussion</DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Discussion Title"
              type="text"
              fullWidth
              variant="outlined"
              name="title"
              value={formik.values.title}
              onKeyDown={(e) => {
                // This stops the 'a', 'd', 'h', 'l' from reaching your global shortcuts
                e.stopPropagation();
              }}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.title && Boolean(formik.errors.title)}
              helperText={formik.touched.title && formik.errors.title}
            />

            <GetUsers formik={formik} setName={setName} currentUser={currentUser} />

            {/* Adding community field */}
            <GetUserCommunities formik={formik} />

            {error && (
              <Typography color="error" variant="body2" style={{ marginTop: '10px' }}>
                {error}
              </Typography>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose} color="primary">
              Cancel
            </Button>
            <Button type="submit" color="primary" disabled={createDiscussionMutation.isLoading}>
              {createDiscussionMutation.isLoading ? 'Creating Discussion...' : 'Create Discussion'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default CreateDiscussionWidget;